import { CardShell } from '@atlas/ui-components'
import { Badge, Button, Skeleton } from '@atlas/ui-primitives'
import { Languages } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { ArticleTranslationVariant } from '@/domain/article.types'
import { isTranslationStuck } from '@/hooks/useArticles'
import { type LocaleLabels, localeLabel } from '@/lib/locale-identity'
import { ArticleTranslationBadge } from './ArticleTranslationBadge'

/** One locale variant: its label, whether it is the group's source, and its job state. */
function VariantChip({
	variant,
	currentArticleId,
	labels,
}: {
	variant: ArticleTranslationVariant
	currentArticleId: string
	labels: LocaleLabels
}) {
	const isCurrent = variant.articleId === currentArticleId
	const label = localeLabel(variant.languageIso, labels)

	return (
		<span className="inline-flex items-center gap-1.5 rounded-ds-full border border-ds-border bg-ds-surface px-3 py-1">
			{isCurrent ? (
				<span className="text-ds-sm font-medium text-ds-text">{label}</span>
			) : (
				<Link
					to={`/articles/${variant.articleId}/edit`}
					className="text-ds-sm font-medium text-ds-primary hover:underline"
				>
					{label}
				</Link>
			)}
			{variant.isSource ? (
				<Badge variant="secondary" size="sm">
					Source
				</Badge>
			) : null}
			{/* Always mounted, not only once a status exists, so the FIRST state ever
			    announced for this variant also reaches assistive tech — a live region
			    that appears alongside the content it announces misses that first
			    announcement in most AT. */}
			<span aria-live="polite" className="contents">
				<ArticleTranslationBadge
					status={variant.translationStatus}
					isStuck={isTranslationStuck(variant.translationStatus, variant.updatedAt)}
				/>
			</span>
		</span>
	)
}

export interface ArticleLocaleStripProps {
	/** The group's live locale variants, source first, as the coverage read returns them. */
	variants: readonly ArticleTranslationVariant[]
	/** The variant whose edit page this is — rendered without a link to itself. */
	currentArticleId: string
	labels: LocaleLabels
	isLoading?: boolean
	/**
	 * Opens the request surface. Omitted when this variant cannot be translated
	 * from — a non-source variant, or a role below the endpoint's gate — so
	 * "no request control" needs no second flag to express.
	 */
	onManage?: () => void
	/**
	 * The group's source, when this is NOT it. Given, the strip says where a
	 * translation is requested from instead of leaving the missing button
	 * unexplained.
	 */
	source?: { articleId: string; languageIso: string }
}

/**
 * An article's locale coverage, above the composer on the edit page.
 *
 * Presentational: it fetches nothing and owns no request state. Placed above the
 * body rather than below it because the composer fills the viewport, and a
 * failed translation an author has to scroll past a whole article to find is a
 * failure they will not find.
 */
export function ArticleLocaleStrip({
	variants,
	currentArticleId,
	labels,
	isLoading = false,
	onManage,
	source,
}: ArticleLocaleStripProps) {
	return (
		<CardShell
			as="section"
			aria-label="Translations"
			className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 p-0 px-4 py-3"
		>
			<span className="inline-flex items-center gap-1.5 font-mono text-ds-2xs uppercase tracking-ds-widest text-ds-text-subtle">
				<Languages className="h-3.5 w-3.5" aria-hidden="true" /> Locales
			</span>

			{isLoading ? (
				<Skeleton shape="text" className="h-6 w-40" />
			) : (
				<div className="flex min-w-0 flex-wrap items-center gap-2">
					{variants.map((variant) => (
						<VariantChip
							key={variant.articleId}
							variant={variant}
							currentArticleId={currentArticleId}
							labels={labels}
						/>
					))}
				</div>
			)}

			<div className="ml-auto flex items-center gap-2">
				{/* Names the source's locale as plain text, not a second link: the chip for
				    that locale is already one, and two identical links side by side give a
				    keyboard or screen-reader user the same destination twice. */}
				{source ? (
					<p className="text-ds-sm text-ds-text-muted">
						Translations are requested from the source, {localeLabel(source.languageIso, labels)}.
					</p>
				) : null}
				{onManage ? (
					<Button variant="secondary" size="sm" className="rounded-ds-full" onPress={onManage}>
						Translate…
					</Button>
				) : null}
			</div>
		</CardShell>
	)
}
